import React, { FunctionComponent, MouseEvent, useCallback, useEffect, useMemo } from 'react';

import { ScopedTheme } from '@yandex-360/orb-components/dist/components/Theme/ScopedTheme';
import { StatusCode } from '@yandex-video-platform/goloom-sdk';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Modal, SvgImage } from '..';
import { selectIsJoined } from '../../../pages/call/selectors';
import { DEFAULT_MODAL_Z_INDEX } from '../../constants';
import {
    getDeviceErrorByKind,
    selectDeviceErrorModalVisibility,
    selectShouldShowDeviceErrorModal,
} from '../../features/device-manager/selectors';
import { DeviceKind, setDeviceErrorModalVisibility } from '../../features/device-manager/slice';
import { DeviceErrorCode } from '../../features/device-manager/types';
import { openSettingsModalAction } from '../../features/settings-modal/slice';
import { getExternalLinks } from '../../helpers/external-links';
import { i18n } from '../../helpers/i18n';
import { createLogger } from '../../helpers/loggerFacade';
import { useActions } from '../../hooks';
import CloseModalButton from '../modal/close-button';
import { ImageName } from '../svg-image/images';
import css from './index.css';

const logger = createLogger('modal-device-error');

type DeviceErrorSet = Set<Maybe<DeviceErrorCode>>;

const HOW_TO_ERRORS: DeviceErrorSet = new Set([
    StatusCode.DEVICE_DENIED_BY_USER,
    StatusCode.DEVICE_DENIED_BY_SYSTEM,
    StatusCode.REQUEST_DEVICES_ERROR,
]);

const HOW_TO_ERRORS_ALTERNATIVE: DeviceErrorSet = new Set([
    StatusCode.DEVICE_BUSY,
    StatusCode.DEVICE_DENIED_BY_SYSTEM,
    StatusCode.DEVICE_DENIED_BY_USER,
]);

const SETTINGS_ERRORS: DeviceErrorSet = new Set([
    StatusCode.DEVICES_NOT_FOUND,
    StatusCode.DEVICE_BUSY,
    StatusCode.DEVICE_NOT_READY,
    StatusCode.APPLYING_DEVICES_ERROR,
]);

const IMAGE_NAME_BY_DEVICE_KIND: Partial<Record<DeviceKind, ImageName>> = {
    audioinput: 'microphoneDeviceError',
    videoinput: 'cameraDeviceError',
};

function getHeaderTextKey(kind: DeviceKind, error: Maybe<DeviceErrorCode>): string {
    if (kind === 'videoinput') {
        switch (error) {
            case StatusCode.DEVICE_NOT_READY:
                return 'modal-device-error-title-not-ready';
            case StatusCode.REQUEST_DEVICES_ERROR:
                return 'modal-device-error-title-request';
            case StatusCode.DEVICE_BUSY:
                return 'modal-device-error-title-videoinput-busy';
            case StatusCode.DEVICE_OVERCONSTRAINED:
                return 'modal-device-error-title-videoinput-overconstrained';
            default:
                return 'modal-device-error-title-videoinput';
        }
    }

    if (kind === 'audioinput') {
        switch (error) {
            case StatusCode.DEVICE_NOT_READY:
                return 'modal-device-error-title-not-ready';
            case StatusCode.REQUEST_DEVICES_ERROR:
                return 'modal-device-error-title-request';
            case StatusCode.DEVICE_BUSY:
                return 'modal-device-error-title-audioinput-busy';
            case StatusCode.DEVICE_DENIED_BY_USER:
            case StatusCode.DEVICE_DENIED_BY_SYSTEM:
                return 'modal-device-error-title-audioinput-sound';
            default:
                return 'modal-device-error-title-audioinput';
        }
    }

    return '';
}

function getContentTextKey(kind: DeviceKind, error: Maybe<DeviceErrorCode>, hasImage: boolean): string {
    if (kind === 'videoinput') {
        switch (error) {
            case StatusCode.DEVICE_NOT_READY:
                return 'modal-device-error-text-not-ready';
            case StatusCode.REQUEST_DEVICES_ERROR:
                if (hasImage) {
                    return 'modal-device-error-text-request-alternative';
                }
                return 'modal-device-error-text-request';
            case StatusCode.DEVICE_BUSY:
                return 'modal-device-error-text-videoinput-busy';
            case StatusCode.DEVICE_OVERCONSTRAINED:
                return 'modal-device-error-text-videoinput-overconstrained';
            case StatusCode.APPLYING_DEVICES_ERROR:
                if (hasImage) {
                    return 'modal-device-error-text-videoinput-applying-alternative';
                }
                return 'modal-device-error-text-videoinput-applying';
            case StatusCode.DEVICE_DENIED_BY_USER:
                return 'modal-device-error-text-videoinput-denied-by-user';
            case StatusCode.DEVICE_DENIED_BY_SYSTEM:
                return 'modal-device-error-text-videoinput-denied-by-system';
            default:
                return 'modal-device-error-text-videoinput';
        }
    }

    if (kind === 'audioinput') {
        switch (error) {
            case StatusCode.DEVICE_NOT_READY:
                return 'modal-device-error-text-not-ready';
            case StatusCode.REQUEST_DEVICES_ERROR:
                if (hasImage) {
                    return 'modal-device-error-text-request-alternative';
                }
                return 'modal-device-error-text-request';
            case StatusCode.DEVICE_BUSY:
                return 'modal-device-error-text-audioinput-busy';
            case StatusCode.DEVICE_DENIED_BY_USER:
                return 'modal-device-error-text-audioinput-denied-by-user';
            case StatusCode.DEVICE_DENIED_BY_SYSTEM:
                return 'modal-device-error-text-audioinput-denied-by-system';
            default:
                return 'modal-device-error-text-audioinput';
        }
    }

    return '';
}

interface Props {
    deviceKind: DeviceKind;
}

export const ModalDeviceError: FunctionComponent<Props> = ({ deviceKind }) => {
    const dispatch = useDispatch();
    const setModalVisibility = useCallback(
        (isVisible: boolean) => {
            dispatch(setDeviceErrorModalVisibility({ kind: deviceKind, isVisible: isVisible }));
        },
        [deviceKind, dispatch]
    );
    const onClose = () => {
        setModalVisibility(false);
    };

    const visibilitySelector = useCallback(
        (state) => {
            return selectDeviceErrorModalVisibility(state, deviceKind);
        },
        [deviceKind]
    );

    const shouldShowSelector = useCallback(
        (state) => {
            return selectShouldShowDeviceErrorModal(state, deviceKind);
        },
        [deviceKind]
    );

    const actions = useActions({ openSettingsModalAction });

    const onClickSettings = useCallback(
        (event: MouseEvent) => {
            actions.openSettingsModalAction({
                isHiddenSectionVisible: event.altKey,
            });
        },
        [actions]
    );

    const urlAccessDenied = useSelector(getExternalLinks).helpNoAccess;
    const deviceError = useSelector((state) => getDeviceErrorByKind(state, deviceKind));
    const isJoined = useSelector(selectIsJoined);
    const shouldShow = useSelector(shouldShowSelector);
    const isVisible = useSelector(visibilitySelector);

    useEffect(() => {
        setModalVisibility(Boolean(shouldShow));
    }, [setModalVisibility, shouldShow]);

    useEffect(() => {
        if (isVisible) {
            logger.event(`Device error modal shown. Code: ${deviceError}`);
        }
    }, [isVisible, deviceError]);

    const imageForError = useMemo(() => {
        // Смотрим только на isVisible и deviceKind, чтобы в процессе создания встречи модалка не меняла внешний вид
        return !isJoined ? IMAGE_NAME_BY_DEVICE_KIND[deviceKind] : null;
    }, [isVisible, deviceKind]); // eslint-disable-line react-hooks/exhaustive-deps

    const headerTextKey = getHeaderTextKey(deviceKind, deviceError);
    const contentTextKey = getContentTextKey(deviceKind, deviceError, Boolean(imageForError));

    if (!isVisible) {
        return null;
    }
    return (
        <ScopedTheme theme="dark">
            <Modal keepMounted onClose={onClose} visible zIndex={DEFAULT_MODAL_Z_INDEX}>
                <div className={css.Container}>
                    {!imageForError ? (
                        <div className={css.Header}>
                            <h3 className={css.HeaderText}>{i18n('statuses', headerTextKey)}</h3>
                            <div className={css.HeaderButtonContainer}>
                                <CloseModalButton onClose={onClose} />
                            </div>
                        </div>
                    ) : null}
                    <div className={css.ContentContainer}>
                        {imageForError ? (
                            <>
                                <SvgImage className={css.ImageContainer} imageName={imageForError} />
                                <h3 className={css.HeaderText}>{i18n('statuses', headerTextKey)}</h3>
                            </>
                        ) : null}
                        <div className={css.ContentText}>{i18n('statuses', contentTextKey)}</div>
                    </div>
                    <div className={css.Footer}>
                        {!imageForError && deviceError && HOW_TO_ERRORS.has(deviceError) ? (
                            <Button view="neutral" size="md" elementType="a" href={urlAccessDenied} target="_blank">
                                {i18n('statuses', 'how-to-do-it')}
                            </Button>
                        ) : null}
                        {!imageForError && deviceError && SETTINGS_ERRORS.has(deviceError) ? (
                            <Button
                                view="neutral"
                                size="md"
                                onClick={(event) => {
                                    onClose();
                                    onClickSettings(event);
                                }}
                            >
                                {i18n('statuses', 'to-settings')}
                            </Button>
                        ) : null}
                        {imageForError && deviceError && HOW_TO_ERRORS_ALTERNATIVE.has(deviceError) ? (
                            <Button view="neutral" size="md" elementType="a" href={urlAccessDenied} target="_blank">
                                {i18n('statuses', 'how-to-do-it')}
                            </Button>
                        ) : null}
                        <Button className={css.ButtonConfirm} view="brand" size="md" onClick={onClose}>
                            {i18n('common', 'got-it')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </ScopedTheme>
    );
};
